///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One layer in the Layers panel catalog or a ⌘K search result, on a
/// single dense line: disclosure (parents with sub-layers), icon, name,
/// load status, the open-table button when the layer has a data browser,
/// and the globe-visibility switch. The description lives in the row
/// tooltip (and inline in palette results, where the match may be in it).
///
/// Colour is state: the icon takes the layer's accent only while the layer
/// is on the globe and is neutral slate otherwise, so scanning the list
/// for colour answers "what's on?". Columns are fixed-width so switches,
/// table buttons and icons line up down the list.
///
/// `rowAction` decides what the row surface does. In the desktop Layers
/// panel it toggles the switch (the data dock on the right shows the
/// table) and the table chip is a real button; on phones and in the
/// palette the row opens the table, as the sheet / palette replaces the
/// list anyway.
///

import { LoadRing } from "@/components/ui/load-ring"
import { Switch } from "@/components/ui/switch"
import { loadStateLabel, useLayerLoadState } from "@/hooks/useLayerLoadStates"
import { useAppSelector } from "@/redux/store"
import type { DatasetType } from "@/types/DatasetElement"
import { cn } from "@/utils/utils"
import { ChevronDown, Table2 } from "lucide-react"
import type { ReactNode } from "react"
import type { LayerDefinition } from "./layerRegistry"

export type LayerRowAction = "toggle" | "open"

interface LayerRowProps {
	layer: LayerDefinition
	visible: boolean
	disabled: boolean
	isMobile: boolean
	navIndex: number
	isSelected: boolean
	/** What clicking the row does (default "open") */
	rowAction?: LayerRowAction
	/** The layer's table is the data dock's active item */
	tableOpen?: boolean
	/** Parent rows: whether the sub-layers / controls are shown; undefined leaves the disclosure out */
	expanded?: boolean
	onToggleExpanded?: (layer: LayerDefinition) => void
	/** Compact state of the layer's controls, shown in the row while they are closed */
	summary?: ReactNode
	/**
	 * Reserve the disclosure and table columns even when this row has
	 * neither, so icons and switches align down a mixed list (the panel)
	 */
	alignColumns?: boolean
	/** Show the description after the name (palette results) */
	showDescription?: boolean
	onToggle: (type: DatasetType, checked: boolean) => void
	onOpenTable: (layer: LayerDefinition) => void
	onHover: () => void
}

/** Table button: quiet at rest, lit while its table is the dock's active item */
function tableButtonClass(active: boolean): string {
	return cn(
		"flex h-6 w-6 items-center justify-center rounded-md flex-shrink-0 border transition-colors",
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50",
		active
			? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
			: "border-transparent text-slate-500 hover:bg-cyan-500/10 hover:border-cyan-500/30 hover:text-cyan-300"
	)
}

export function LayerRow({
	layer, visible, disabled, isMobile, navIndex, isSelected,
	rowAction = "open", tableOpen = false, expanded, onToggleExpanded, summary,
	alignColumns = false, showDescription = false,
	onToggle, onOpenTable, onHover,
}: LayerRowProps) {
	const hasTable = !!layer.commandId && !disabled
	const hasDisclosure = expanded !== undefined && onToggleExpanded !== undefined
	const isChild = !!layer.parentId
	const canToggle = !!layer.visibility && !disabled

	const rowOpens = rowAction === "open" && hasTable
	const rowToggles = rowAction === "toggle" && canToggle
	const rowInteractive = rowOpens || rowToggles

	// Data still coming for this layer: ring the icon and, while the layer
	// is on the globe, say what for beside the name so an empty globe isn't
	// read as "no data". Hidden layers syncing only get the ring
	const loadState = useLayerLoadState(layer.visibility)
	const timeZoneMode = useAppSelector(state => state.earthbrowser.timeZoneMode)
	const statusText = loadState && visible ? loadStateLabel(loadState, timeZoneMode) : null

	const activateRow = () => {
		if (rowOpens) onOpenTable(layer)
		else if (rowToggles) onToggle(layer.visibility!, !visible)
	}

	const actionLabel = rowOpens
		? `Open ${layer.name} table`
		: rowToggles
			? (visible ? `Hide ${layer.name}` : `Show ${layer.name}`)
			: null
	const rowTitle = actionLabel ? `${actionLabel}\n${layer.description}` : layer.description

	return (
		<div
			data-nav-index={navIndex}
			onClick={rowInteractive ? activateRow : undefined}
			onMouseEnter={onHover}
			role={rowInteractive ? "button" : undefined}
			tabIndex={rowInteractive ? 0 : undefined}
			onKeyDown={rowInteractive ? (e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					e.stopPropagation()
					activateRow()
				}
			} : undefined}
			className={cn(
				"group flex items-center gap-2 rounded-md px-2 transition-colors",
				isMobile ? (isChild ? "h-9" : "h-10") : (isChild ? "h-7" : "h-8"),
				// Sub-layer icons sit under the parent's name
				isChild && "ml-6",
				rowInteractive && "cursor-pointer hover:bg-white/5",
				isSelected && "bg-white/10",
				disabled && "opacity-45"
			)}
			title={rowTitle}
		>
			{hasDisclosure ? (
				<button
					onClick={e => {
						e.stopPropagation()
						onToggleExpanded(layer)
					}}
					className={cn(
						"flex h-4 w-3.5 items-center justify-center rounded-sm flex-shrink-0 text-slate-500",
						"hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50"
					)}
					aria-expanded={expanded}
					aria-label={expanded ? `Hide ${layer.name} options` : `Show ${layer.name} options`}
				>
					<ChevronDown className={cn("h-3 w-3 transition-transform duration-300", !expanded && "-rotate-90")} />
				</button>
			) : alignColumns ? (
				<span className="w-3.5 flex-shrink-0" aria-hidden="true" />
			) : null}
			<span
				className={cn(
					"relative flex h-4 w-4 items-center justify-center flex-shrink-0 [&>svg:not([data-load-ring])]:h-4 [&>svg:not([data-load-ring])]:w-4",
					!visible && "[&>svg:not([data-load-ring])]:text-slate-500"
				)}
			>
				{layer.icon}
				<LoadRing state={loadState} strokeWidth={2.5} />
			</span>
			<div className="flex-1 min-w-0 flex items-baseline gap-2">
				<span className={cn(
					"truncate font-medium",
					// Beside a description the name keeps its full width and
					// the description gives way (palette rows are wide)
					showDescription && "flex-shrink-0 max-w-[60%]",
					isChild ? "text-[13px]" : "text-sm",
					visible ? "text-white" : "text-slate-300"
				)}>
					{layer.name}
				</span>
				{showDescription && (
					<span className="hidden sm:inline truncate text-xs text-slate-500">{layer.description}</span>
				)}
			</div>
			{statusText ? (
				<span
					className={cn(
						"flex-shrink min-w-0 max-w-[45%] truncate text-[11px]",
						loadState?.phase === "error" && !loadState.done ? "text-amber-300/80" : "text-cyan-300/80"
					)}
				>
					{statusText}
				</span>
			) : summary && expanded === false ? (
				// Controls closed: their (short) summary takes the status slot
				// and keeps its width, so the name truncates before it does
				<span className="flex flex-shrink-0">{summary}</span>
			) : null}
			{hasTable && rowAction === "toggle" ? (
				// Real button: the row itself toggles, so this is the only way
				// to the table from here
				<button
					onClick={e => {
						e.stopPropagation()
						onOpenTable(layer)
					}}
					className={tableButtonClass(tableOpen)}
					aria-label={tableOpen ? `${layer.name} table is open` : `Open ${layer.name} table`}
					title={tableOpen ? `${layer.name} table is open in the dock` : `Open ${layer.name} table (⌘↵)`}
				>
					<Table2 className="h-3.5 w-3.5" />
				</button>
			) : hasTable ? (
				<div
					className={cn(tableButtonClass(false), "group-hover:text-cyan-300 group-hover:bg-cyan-500/10 group-hover:border-cyan-500/30")}
					aria-hidden="true"
				>
					<Table2 className="h-3.5 w-3.5" />
				</div>
			) : alignColumns ? (
				<span className="w-6 flex-shrink-0" aria-hidden="true" />
			) : null}
			{layer.visibility && (
				<div onClick={e => e.stopPropagation()} className="flex-shrink-0">
					<Switch
						// On phones the row opens the table, so the switch is
						// the only toggle and keeps its full touch size
						size={isMobile ? "default" : "sm"}
						checked={visible}
						disabled={disabled}
						onCheckedChange={checked => onToggle(layer.visibility!, checked)}
						aria-label={`Show ${layer.name} on globe`}
					/>
				</div>
			)}
		</div>
	)
}
